import { Box, FormControlSizes, HStack, Icon, IconButton, IconName, IndicatorBadge, Text, Tooltip } from '@talos/kyoko';
import { useCallback, type MouseEvent, type SyntheticEvent } from 'react';
import { useTheme } from 'styled-components';
import { MakeDefaultMarketAccountButton } from '../../../DefaultMarketAccount/styles';
import { useMarketSelectorContext } from '../../MarketSelectorContext';
import type { MarketSelectorCustomPresetItem } from '../../types';
import { PresetListItemRow } from '../styles';

const DEFAULT_AGGREGATION_TOOLTIP =
  'The default aggregation will be selected when creating a new market card, priming orders or a DeepDive from the WatchList or selecting/changing symbol on New Order, New RFQ, Market Card and the DeepDive. The aggregation will be used if it contains market accounts that are valid for the selected symbol.';

const CLEAR_DEFAULT_AGGREGATION_TOOLTIP = 'Remove default aggregation';

export function AggregationPresetRow({
  presetID,
  selection,
  favorite,
  onFavoriteClicked,
  customPresetItemsByID,
  defaultAggregationId,
  onDefaultAggregationChange,
  rowTestId,
  pinnedDefaultRow = false,
}: {
  presetID: string;
  selection: string | undefined;
  favorite: boolean;
  onFavoriteClicked: () => void;
  customPresetItemsByID: Map<string, MarketSelectorCustomPresetItem>;
  defaultAggregationId: string | undefined;
  onDefaultAggregationChange: (id: string | undefined) => void;
  rowTestId: string;
  pinnedDefaultRow?: boolean;
}) {
  const theme = useTheme();
  const { presetsByID, isOpen } = useMarketSelectorContext();
  const selected = selection === presetID;
  const isCustom = customPresetItemsByID.has(presetID);
  const isDefault = defaultAggregationId === presetID;

  const handleFavoriteClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      onFavoriteClicked();
    },
    [onFavoriteClicked]
  );

  const handleMakeDefaultClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      onDefaultAggregationChange(presetID);
    },
    [onDefaultAggregationChange, presetID]
  );

  const handleClearDefaultStopBubble = useCallback((e: SyntheticEvent) => {
    e.stopPropagation();
  }, []);

  const handleClearDefaultClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      onDefaultAggregationChange(undefined);
    },
    [onDefaultAggregationChange]
  );

  const label = customPresetItemsByID.get(presetID)?.label ?? presetsByID?.get(presetID)?.label;
  if (!label) {
    return null;
  }

  const showDefaultActionSlot = isOpen && !isCustom && (!pinnedDefaultRow || isDefault);
  const outerDataVisible = isDefault && !pinnedDefaultRow ? 'true' : 'false';

  const clearDefaultControl = (
    <span className="preset-default-clear">
      <Tooltip tooltip={CLEAR_DEFAULT_AGGREGATION_TOOLTIP}>
        <IconButton
          icon={IconName.Close}
          size={FormControlSizes.Xxs}
          dim
          aria-label="Remove default aggregation"
          data-testid="default-aggregation-clear-button"
          onPointerDown={handleClearDefaultStopBubble}
          onMouseDown={handleClearDefaultStopBubble}
          onClick={handleClearDefaultClick}
        />
      </Tooltip>
    </span>
  );

  return (
    <PresetListItemRow data-testid={rowTestId}>
      <HStack gap="spacing8" flex={1} minWidth={0} overflow="hidden" justifyContent="flex-start">
        {!isCustom && (
          <Icon
            icon={favorite ? IconName.StarSolid : IconName.Star}
            size={14}
            color={favorite ? theme.colors.yellow.default : theme.colorTextMuted}
            onClick={handleFavoriteClick}
          />
        )}
        <Text textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden" color="colorTextImportant">
          {label}
        </Text>
      </HStack>
      <HStack gap="spacing4" alignItems="center" flex="none">
        {showDefaultActionSlot && pinnedDefaultRow && isDefault && (
          <Box className="preset-default-slot" data-visible="false" onClick={e => e.stopPropagation()}>
            {clearDefaultControl}
          </Box>
        )}
        {showDefaultActionSlot && !pinnedDefaultRow && (
          <Box className="preset-default-slot" data-visible={outerDataVisible} onClick={e => e.stopPropagation()}>
            {isDefault ? (
              <HStack gap="spacing4" alignItems="center">
                <IndicatorBadge data-testid="default-aggregation-indicator">Default</IndicatorBadge>
                {clearDefaultControl}
              </HStack>
            ) : (
              <Tooltip tooltip={DEFAULT_AGGREGATION_TOOLTIP}>
                <MakeDefaultMarketAccountButton
                  icon={IconName.Check}
                  size={FormControlSizes.Xxs}
                  aria-label="Make default aggregation"
                  data-testid="default-aggregation-make-default-button"
                  onClick={handleMakeDefaultClick}
                />
              </Tooltip>
            )}
          </Box>
        )}
        {selected && <Icon icon={IconName.ChevronRight} />}
      </HStack>
    </PresetListItemRow>
  );
}
